import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSucursal } from '../context/SucursalContext';
import { cajaApi } from '../services/api';
import { imprimirReciboMovimiento, imprimirTicketCierre } from '../utils/printHelpers';
import { formatDateTime } from '../utils/dateHelpers';

const CajaDiaria = () => {
  const { user, isAdmin } = useAuth();
  const { sucursales, sucursalActual, cambiarSucursal } = useSucursal();
  const tiendaIdParaCaja = isAdmin() ? sucursalActual?.id : user?.tienda_id;
  const [caja, setCaja] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [montoApertura, setMontoApertura] = useState('0');
  const [editandoApertura, setEditandoApertura] = useState(false);
  const [nuevoMontoApertura, setNuevoMontoApertura] = useState('');
  const [montoCierre, setMontoCierre] = useState('');
  const [observacionesCierre, setObservacionesCierre] = useState('');

  const [nuevoMovimiento, setNuevoMovimiento] = useState({
    tipo: 'ingreso',
    monto: '',
    concepto: '',
    metodo_pago: 'Efectivo',
    cuenta_bancaria_id: '',
    pos_id: '',
    categoria_egreso_id: '',
    comprobante: ''
  });

  const [cuentas, setCuentas] = useState([]);
  const [pos, setPos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [showMovimientoModal, setShowMovimientoModal] = useState(false);
  const [showMovimientosLista, setShowMovimientosLista] = useState(false);

  useEffect(() => {
    cargarCaja();
    cargarCatalogos();
  }, [tiendaIdParaCaja]);

  const cargarCaja = async () => {
    if (!tiendaIdParaCaja || tiendaIdParaCaja === 'todas') {
      setCaja(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await cajaApi.getEstado(tiendaIdParaCaja);
      if (data.abierta) {
        setCaja(data.caja);
        cargarMovimientos(data.caja.id);
      } else {
        setCaja(null);
      }
    } catch (error) {
      console.error('Error cargando caja', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarMovimientos = async (caja_id) => {
    try {
      const data = await cajaApi.getMovimientos({ caja_id });
      setMovimientos(data || []);
    } catch (error) {
      console.error('Error cargando movimientos', error);
    }
  };

  const cargarCatalogos = async () => {
    if (!tiendaIdParaCaja || tiendaIdParaCaja === 'todas') return;
    try {
      const [resCuentas, resPos, resCategorias] = await Promise.all([
        cajaApi.getCuentas(tiendaIdParaCaja),
        cajaApi.getPos(tiendaIdParaCaja),
        cajaApi.getCategorias(tiendaIdParaCaja)
      ]);
      setCuentas(resCuentas || []);
      setPos(resPos || []);
      setCategorias(resCategorias || []);
    } catch (error) {
      console.error('Error cargando catalogos', error);
    }
  };

  const handleAbrirCaja = async (e) => {
    e.preventDefault();
    try {
      await cajaApi.abrirCaja({
        tienda_id: tiendaIdParaCaja,
        usuario_id: user.id,
        monto_apertura: montoApertura
      });
      setMontoApertura('0');
      cargarCaja();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleGuardarApertura = async (e) => {
    e.preventDefault();
    try {
      await cajaApi.actualizarMontoApertura({
        caja_id: caja.id,
        monto_apertura: nuevoMontoApertura
      });
      setEditandoApertura(false);
      cargarCaja();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCerrarCaja = async (e) => {
    e.preventDefault();
    const totalCalculado = (parseFloat(caja.monto_apertura) +
                  parseFloat(caja.totales?.ingresos_efectivo || 0) +
                  parseFloat(caja.totales?.ingresos_transferencia || 0) +
                  parseFloat(caja.totales?.ingresos_pos || 0) +
                  parseFloat(caja.totales?.ingresos_cheque || 0) -
                  parseFloat(caja.totales?.total_egresos || 0)).toFixed(2);
    if (montoCierre === '' || montoCierre === null) {
      alert('Debe ingresar el monto real contado en caja para poder cerrarla.');
      return;
    }

    const cierreReal = parseFloat(montoCierre);

    if (isNaN(cierreReal) || cierreReal < 0) {
      alert('El monto real en caja no es válido.');
      return;
    }

    if (cierreReal.toFixed(2) !== totalCalculado) {
        if (!observacionesCierre || observacionesCierre.trim() === '') {
            alert('El monto real no coincide con el calculado. Por favor, ingrese una justificación en observaciones.');
            return;
        }
    }

    if (!window.confirm('¿Está seguro de cerrar la caja?')) return;
    try {
      await cajaApi.cerrarCaja({
        caja_id: caja.id,
        monto_cierre: montoCierre,
        observaciones: observacionesCierre
      });

      imprimirTicketCierre(
        {
          ...caja,
          estado: 'cerrada',
          fecha_cierre: new Date().toISOString(),
          monto_cierre: montoCierre !== '' ? montoCierre : totalCalculado
        },
        sucursales.find(s => s.id === tiendaIdParaCaja)?.nombre,
        movimientos
      );

      setMontoCierre('');
      setObservacionesCierre('');
      cargarCaja();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const abrirModalMovimiento = (tipo) => {
    setNuevoMovimiento({
      tipo, monto: '', concepto: '', metodo_pago: 'Efectivo',
      cuenta_bancaria_id: '', pos_id: '', categoria_egreso_id: '', comprobante: ''
    });
    setShowMovimientoModal(true);
  };

  const cerrarModalMovimiento = () => {
    setShowMovimientoModal(false);
  };

  const handleMovimientoSubmit = async (e) => {
    e.preventDefault();
    try {
      await cajaApi.registrarMovimiento({
        ...nuevoMovimiento,
        caja_id: caja.id,
        usuario_id: user.id
      });
      setShowMovimientoModal(false);
      cargarCaja(); // Recargar caja para ver nuevos totales y movimientos
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const formatMoney = (amount) => `Q${parseFloat(amount || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;

  if (isAdmin() && (!tiendaIdParaCaja || tiendaIdParaCaja === 'todas')) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Caja Diaria</h1>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <h3>Seleccione una sucursal</h3>
          <p style={{ color: 'var(--text-light)', marginBottom: '16px' }}>
            Para ver u operar la caja diaria, debe seleccionar una sucursal específica, no "Todas las Sucursales".
          </p>
          <select
            className="form-control"
            style={{ maxWidth: '300px', margin: '0 auto' }}
            value={sucursalActual?.id || 'todas'}
            onChange={(e) => cambiarSucursal(e.target.value)}
          >
            <option value="todas">Todas las Sucursales</option>
            {sucursales.map(s => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  if (!isAdmin() && !tiendaIdParaCaja) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Caja Diaria</h1>
        </div>
        <div className="card" style={{ textAlign: 'center', color: 'var(--danger-color)' }}>
          <h3>Sucursal no asignada</h3>
          <p>Tu usuario no tiene una sucursal asignada en el sistema. Debes tener una sucursal para poder operar la caja diaria.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Caja Diaria</h1>
          <p className="page-subtitle" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {sucursales.find(s => s.id === tiendaIdParaCaja)?.nombre || ''}
            {caja && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: 'var(--success-color)' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--success-color)', display: 'inline-block' }}></span>
                Caja Abierta
              </span>
            )}
          </p>
        </div>
        {isAdmin() && (
          <select
            className="form-control"
            style={{ width: 'auto', minWidth: '200px' }}
            value={sucursalActual?.id || 'todas'}
            onChange={(e) => cambiarSucursal(e.target.value)}
          >
            <option value="todas">Todas las Sucursales</option>
            {sucursales.map(s => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        )}
      </div>

      {!caja ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '32px' }}>
          <div className="card" style={{ maxWidth: '440px', width: '100%', textAlign: 'center' }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: 'var(--primary-color)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px', fontSize: '26px'
            }}>
              🔓
            </div>
            <h3 className="card-title" style={{ marginBottom: '4px' }}>Abrir Caja</h3>
            <p style={{ color: 'var(--text-light)', fontSize: '13px', marginBottom: '20px' }}>
              {sucursales.find(s => s.id === tiendaIdParaCaja)?.nombre || ''}
            </p>

            <form onSubmit={handleAbrirCaja}>
              <div className="form-group" style={{ textAlign: 'left' }}>
                <label className="form-label required">Monto de Apertura (Q)</label>
                <input
                  type="number"
                  className="form-control"
                  value={montoApertura}
                  onChange={(e) => setMontoApertura(e.target.value)}
                  required
                  step="0.01"
                  min="0"
                  autoFocus
                />
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
                Abrir Caja
              </button>
            </form>
          </div>
        </div>
      ) : (
        <>
          {caja.observaciones?.includes('Apertura automática') && (
            <div style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              fontSize: '13px',
              color: '#1e40af'
            }}>
              <span>
                <strong>ℹ️ Apertura automática:</strong> Esta caja se abrió automáticamente al registrarse un cobro sin una caja previa abierta.
                {parseFloat(caja.monto_apertura) === 0 && ' Si iniciaste con un fondo de caja inicial, puedes ajustarlo con el botón "Ajustar Fondo Inicial".'}
              </span>
              {parseFloat(caja.monto_apertura) === 0 && !editandoApertura && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ whiteSpace: 'nowrap' }}
                  onClick={() => {
                    setNuevoMontoApertura(caja.monto_apertura);
                    setEditandoApertura(true);
                  }}
                >
                  Ajustar Fondo Inicial
                </button>
              )}
            </div>
          )}

          {/* Hero: Total calculado + acciones rápidas */}
          <div className="card" style={{ marginBottom: '24px', background: 'linear-gradient(135deg, var(--primary-color), #1e40af)', color: '#fff' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '20px' }}>
              <div>
                <span style={{ fontSize: '13px', opacity: 0.85 }}>Total Calculado en Caja</span>
                <h1 style={{ color: '#fff', margin: '4px 0 0', fontSize: '36px' }}>
                  {formatMoney(
                    parseFloat(caja.monto_apertura) +
                    parseFloat(caja.totales?.ingresos_efectivo || 0) +
                    parseFloat(caja.totales?.ingresos_transferencia || 0) +
                    parseFloat(caja.totales?.ingresos_pos || 0) +
                    parseFloat(caja.totales?.ingresos_cheque || 0) -
                    parseFloat(caja.totales?.total_egresos || 0)
                  )}
                </h1>
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn"
                  style={{ background: 'var(--success-color)', borderColor: 'var(--success-color)', color: '#fff' }}
                  onClick={() => abrirModalMovimiento('ingreso')}
                >
                  + Ingreso
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => abrirModalMovimiento('egreso')}
                >
                  + Egreso
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowMovimientosLista(true)}
                >
                  Ver Movimientos ({movimientos.length})
                </button>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '24px', alignItems: 'start' }}>
            {/* Columna izquierda: totales del día */}
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '16px' }}>Totales del Día</h3>
              <div className="stats-grid" style={{ gridTemplateColumns: '1fr 1fr', marginBottom: 0 }}>
                <div className="stat-card">
                  <div className="stat-info">
                    {editandoApertura ? (
                      <form onSubmit={handleGuardarApertura} style={{ display: 'flex', gap: '4px', alignItems: 'center', marginBottom: '4px' }}>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-control"
                          style={{ width: '90px', padding: '2px 6px', height: '28px', fontSize: '13px' }}
                          value={nuevoMontoApertura}
                          onChange={(e) => setNuevoMontoApertura(e.target.value)}
                          autoFocus
                          required
                        />
                        <button type="submit" className="btn btn-primary btn-sm" style={{ padding: '2px 8px', fontSize: '12px' }} title="Guardar">✓</button>
                        <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '2px 6px', fontSize: '12px' }} onClick={() => setEditandoApertura(false)} title="Cancelar">✕</button>
                      </form>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <h3 style={{ fontSize: '18px', margin: 0 }}>{formatMoney(caja.monto_apertura)}</h3>
                        <button
                          type="button"
                          title="Ajustar monto de apertura"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: '11px', color: 'var(--primary-color)', padding: 0, textDecoration: 'underline'
                          }}
                          onClick={() => {
                            setNuevoMontoApertura(caja.monto_apertura);
                            setEditandoApertura(true);
                          }}
                        >
                          editar
                        </button>
                      </div>
                    )}
                    <p style={{ margin: 0 }}>Apertura</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-info">
                    <h3 style={{ fontSize: '18px' }}>{formatMoney(caja.totales?.ingresos_efectivo)}</h3>
                    <p>Efectivo</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-info">
                    <h3 style={{ fontSize: '18px' }}>{formatMoney(caja.totales?.ingresos_transferencia)}</h3>
                    <p>Transferencia</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-info">
                    <h3 style={{ fontSize: '18px' }}>{formatMoney(caja.totales?.ingresos_pos)}</h3>
                    <p>POS</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-info">
                    <h3 style={{ fontSize: '18px' }}>{formatMoney(caja.totales?.ingresos_cheque)}</h3>
                    <p>Cheque</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-info">
                    <h3 style={{ fontSize: '18px', color: 'var(--danger-color)' }}>{formatMoney(caja.totales?.total_egresos)}</h3>
                    <p>Egresos</p>
                  </div>
                </div>
              </div>

              {((caja.desglose_transferencias?.length > 0) || (caja.desglose_pos?.length > 0)) && (
                <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                  <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-light)' }}>Desglose por Cuenta / Terminal</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    {caja.desglose_transferencias?.length > 0 && (
                      <div>
                        <p style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Transferencia</p>
                        {caja.desglose_transferencias.map(d => (
                          <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '2px' }}>
                            <span>{d.nombre} {d.banco ? `(${d.banco})` : ''}</span>
                            <span style={{ fontWeight: 600 }}>{formatMoney(d.total)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {caja.desglose_pos?.length > 0 && (
                      <div>
                        <p style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>POS</p>
                        {caja.desglose_pos.map(d => (
                          <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '2px' }}>
                            <span>{d.nombre}</span>
                            <span style={{ fontWeight: 600 }}>{formatMoney(d.total)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Columna derecha: Cerrar caja */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div className="card" style={{ borderLeft: '4px solid var(--danger-color)' }}>
                <h3 className="card-title" style={{ marginBottom: '4px', color: 'var(--danger-color)' }}>Cerrar Caja</h3>
                <p style={{ color: 'var(--text-light)', fontSize: '13px', marginBottom: '16px' }}>
                  Acción final del día. No se podrán registrar más movimientos hasta abrir una nueva caja.
                </p>
                <form onSubmit={handleCerrarCaja}>
                  <div className="form-group">
                    <label className="form-label required">Monto Real en Caja</label>
                    <input
                      type="number"
                      className="form-control"
                      value={montoCierre}
                      onChange={(e) => setMontoCierre(e.target.value)}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Observaciones (Requerido si hay diferencia)</label>
                    <textarea
                      className="form-control"
                      value={observacionesCierre}
                      onChange={(e) => setObservacionesCierre(e.target.value)}
                      rows="2"
                    />
                  </div>
                  <button type="submit" className="btn btn-danger" style={{ width: '100%' }}>
                    Cerrar Caja
                  </button>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal Movimientos del Día */}
      {showMovimientosLista && (
        <div className="modal-overlay" onClick={() => setShowMovimientosLista(false)}>
          <div className="modal" style={{ maxWidth: '900px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Movimientos del Día</h2>
              <button className="modal-close" onClick={() => setShowMovimientosLista(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {movimientos.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No hay movimientos registrados</p>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Tipo</th>
                        <th>Concepto</th>
                        <th>Método</th>
                        <th>Monto</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {movimientos.map(m => (
                        <tr key={m.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(m.fecha)}</td>
                          <td>
                            <span className={`status-badge ${m.tipo === 'ingreso' ? 'status-ingreso' : 'status-egreso'}`}>
                              {m.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}
                            </span>
                          </td>
                          <td>
                            {m.concepto}
                            {m.orden_id && <div><small style={{ color: 'var(--text-light)' }}>Orden #{m.orden_id}</small></div>}
                          </td>
                          <td>
                            {m.metodo_pago}
                            {m.cuenta_nombre && <small style={{ color: 'var(--text-light)' }}> ({m.cuenta_nombre})</small>}
                            {m.pos_nombre && <small style={{ color: 'var(--text-light)' }}> ({m.pos_nombre})</small>}
                            {m.categoria_nombre && <small style={{ color: 'var(--text-light)' }}> [{m.categoria_nombre}]</small>}
                          </td>
                          <td style={{ fontWeight: 600, color: m.tipo === 'ingreso' ? 'var(--success-color)' : 'var(--danger-color)' }}>
                            {m.tipo === 'ingreso' ? '+' : '-'} {formatMoney(m.monto)}
                          </td>
                          <td>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => imprimirReciboMovimiento(m, sucursales.find(s => s.id === tiendaIdParaCaja)?.nombre)}
                            >
                              Imprimir
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Registrar Movimiento */}
      {showMovimientoModal && (
        <div className="modal-overlay" onClick={cerrarModalMovimiento}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {nuevoMovimiento.tipo === 'ingreso' ? 'Registrar Ingreso' : 'Registrar Egreso'}
              </h2>
              <button className="modal-close" onClick={cerrarModalMovimiento}>&times;</button>
            </div>
            <form onSubmit={handleMovimientoSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Concepto</label>
                  <input
                    type="text"
                    className="form-control"
                    required
                    autoFocus
                    value={nuevoMovimiento.concepto}
                    onChange={(e) => setNuevoMovimiento({ ...nuevoMovimiento, concepto: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Monto (Q)</label>
                  <input
                    type="number"
                    className="form-control"
                    required
                    step="0.01"
                    min="0.01"
                    value={nuevoMovimiento.monto}
                    onChange={(e) => setNuevoMovimiento({ ...nuevoMovimiento, monto: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Método de Pago</label>
                  <select
                    className="form-control"
                    value={nuevoMovimiento.metodo_pago}
                    onChange={(e) => setNuevoMovimiento({ ...nuevoMovimiento, metodo_pago: e.target.value })}
                  >
                    <option value="Efectivo">Efectivo</option>
                    <option value="Transferencia">Transferencia</option>
                    <option value="POS">POS</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                </div>

                {nuevoMovimiento.metodo_pago === 'Transferencia' && (
                  <div className="form-group">
                    <label className="form-label">Cuenta Bancaria</label>
                    <select className="form-control" value={nuevoMovimiento.cuenta_bancaria_id} onChange={(e) => setNuevoMovimiento({ ...nuevoMovimiento, cuenta_bancaria_id: e.target.value })} required>
                      <option value="">-- Seleccione --</option>
                      {cuentas.map(c => <option key={c.id} value={c.id}>{c.nombre} - {c.banco}</option>)}
                    </select>
                  </div>
                )}

                {nuevoMovimiento.metodo_pago === 'POS' && (
                  <div className="form-group">
                    <label className="form-label">Terminal POS</label>
                    <select className="form-control" value={nuevoMovimiento.pos_id} onChange={(e) => setNuevoMovimiento({ ...nuevoMovimiento, pos_id: e.target.value })} required>
                      <option value="">-- Seleccione --</option>
                      {pos.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                    </select>
                  </div>
                )}

                {nuevoMovimiento.metodo_pago === 'Cheque' && (
                  <div className="form-group">
                    <label className="form-label">No. Cheque / Referencia / Banco</label>
                    <input
                      type="text"
                      className="form-control"
                      required
                      value={nuevoMovimiento.comprobante}
                      onChange={(e) => setNuevoMovimiento({ ...nuevoMovimiento, comprobante: e.target.value })}
                    />
                  </div>
                )}

                {nuevoMovimiento.tipo === 'egreso' && (
                  <div className="form-group">
                    <label className="form-label">Categoría de Egreso</label>
                    <select className="form-control" value={nuevoMovimiento.categoria_egreso_id} onChange={(e) => setNuevoMovimiento({ ...nuevoMovimiento, categoria_egreso_id: e.target.value })} required>
                      <option value="">-- Seleccione --</option>
                      {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={cerrarModalMovimiento}>
                  Cancelar
                </button>
                <button type="submit" className={`btn ${nuevoMovimiento.tipo === 'ingreso' ? 'btn-primary' : 'btn-danger'}`}>
                  Guardar {nuevoMovimiento.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CajaDiaria;
